import {useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import utils from 'helpers/utils';
import constants from 'helpers/constants';
import {useEffectEvent} from 'helpers/hooks/utils';
import {useLinkNavigate} from 'helpers/hooks/links';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';

export function useTableControl (pathPrefix, pathPostfix, tablePostfix, fetchMore) {
  const tableProvider = useTable();
  const selected = tableProvider.selected();
  const loaded = !tableProvider.isLoading() && utils.isDefined(tableProvider?.list?.data);
  const empty = tableProvider?.list?.meta?.resultsCount === 0;
  const dataKey = tableProvider.dataKey;
  const reset = tableProvider.state?.reset;

  const loadedEmpty = loaded && empty;
  const tail = pathPostfix ? pathPostfix.split('/') : null;
  const isItemPage = tail?.length > 0 ? utils.isNumber(tail[0]) : false;
  const postFix = isItemPage ? `${tail.slice(1).join('/')}` : '';

  const selectedItemId = selected?.[dataKey];
  const pathItemId = isItemPage ? tail[0] : null;
  const itemError = isItemPage && (+pathItemId === 0 || tableProvider.data?.status?.hasError);
  const itemLoaded = isItemPage && +selectedItemId === +pathItemId;

  const navigate = useLinkNavigate();

  const resetRef = useRef(null);
  const fetchingMoreRef = useRef({});
  const lastKnownSelectedIndex = useRef(null);
  const firstEvent = useEffectEvent(tableProvider.first);
  const findEvent = useEffectEvent(tableProvider.find);
  const nextEvent = useEffectEvent(tableProvider.next);
  const prevEvent = useEffectEvent(tableProvider.prev);
  const preloadEvent = useEffectEvent(tableProvider.preload);
  const compareEvent = useEffectEvent(tableProvider.onCompare);
  const fetchNextEvent = useEffectEvent(tableProvider.list?.query?.fetchNextPage);

  const debouncedNavigate = useMemo(() => {
    return utils.debounce((navigation) => {
      navigate(navigation);
    }, constants.debounce.reset);
  }, [navigate]);

  useLayoutEffect(() => {
    if (isItemPage && (itemLoaded || itemError) && loaded) {
      if (reset !== resetRef.current) {
        const firstItemId = firstEvent()?.[dataKey];
        const findItemId = findEvent()?.[dataKey];
        lastKnownSelectedIndex.current = null;

        if (findItemId) {
          debouncedNavigate({
            to: `${pathPrefix}/${findItemId}${postFix ? `/${postFix}` : ''}`,
            replace: true,
            keepSearchParams: true
          });
        } else if (firstItemId) {
          debouncedNavigate({
            to: `${pathPrefix}/${firstItemId}${postFix ? `/${postFix}` : ''}`,
            replace: true,
            keepSearchParams: true
          });
        }
      }
      resetRef.current = reset;
    }
  }, [reset, itemLoaded, itemError, loaded, isItemPage, pathPrefix, postFix,
    dataKey, firstEvent, findEvent, debouncedNavigate]);

  useLayoutEffect(() => {
    if (isItemPage && loaded) {
      if (loadedEmpty) {
        lastKnownSelectedIndex.current = null;
        debouncedNavigate({
          to: `${pathPrefix}${tablePostfix ? `/${tablePostfix}` : ''}`,
          replace: true,
          keepSearchParams: true
        });
      }
    }
  }, [loaded, loadedEmpty, selected, isItemPage, debouncedNavigate, pathPrefix, tablePostfix]);

  useLayoutEffect(() => {
    if (isItemPage && itemLoaded && loaded && utils.isDefined(selected)) {
      const selectedIndex = (tableProvider?.list?.data ?? []).findIndex((item) => compareEvent?.(item, selected));

      if (selectedIndex !== -1) {
        lastKnownSelectedIndex.current = selectedIndex;
      }
    }
  }, [isItemPage, itemLoaded, loaded, tableProvider?.list?.data, selected, compareEvent]);

  useLayoutEffect(() => {
    const selectItem = (item) => {
      debouncedNavigate({
        to: `${pathPrefix}/${item[dataKey]}${postFix ? `/${postFix}` : ''}`,
        replace: true,
        keepSearchParams: true
      });
    };

    if (isItemPage && itemLoaded && loaded) {
      if (tableProvider?.list?.data?.length > 0 && utils.isDefined(selected)) {
        const selectedIndex = tableProvider.list.data.findIndex((item) => compareEvent?.(item, selected));
        if (selectedIndex === -1 && utils.isDefined(lastKnownSelectedIndex.current)) {
          if (lastKnownSelectedIndex.current > (tableProvider?.list?.data?.length - 1)) {
            selectItem(tableProvider?.list?.data[tableProvider?.list?.data?.length - 1]);
          } else if (lastKnownSelectedIndex.current < 0) {
            selectItem(tableProvider?.list?.data[0]);
          } else if (lastKnownSelectedIndex.current >= 0) {
            selectItem(tableProvider?.list?.data[lastKnownSelectedIndex.current]);
          }
        }
      }
    }
  }, [isItemPage, itemLoaded, loaded, tableProvider?.list?.data, selected, dataKey,
    findEvent, compareEvent, pathPrefix, postFix, debouncedNavigate]);

  useEffect(() => {
    return utils.observeTimeout(() => {
      if (isItemPage && itemLoaded && loaded && utils.isDefined(selected)) {
        const nextItems = utils.toArray(nextEvent?.(1));
        const prevItems = utils.toArray(prevEvent?.(1));

        preloadEvent?.(prevItems.filter((e) => {
          return !compareEvent?.(e, selected) &&
            !nextItems.find((ep) => compareEvent?.(ep, e));
        })
          .concat(nextItems.filter((e) => !compareEvent?.(e, selected)))
          .map((item) => item[dataKey]));
      }
    }, constants.debounce.preload);
  }, [isItemPage, itemLoaded, loaded, dataKey, nextEvent, prevEvent, selected, preloadEvent, compareEvent]);

  const fetching = (tableProvider.list?.status?.isLoading || tableProvider.list?.status?.isLoadingNext);
  useEffect(() => {
    if (fetchMore && isItemPage && utils.isDefined(selected)) {
      if (loaded) {
        fetchingMoreRef.current.startedFetching = fetchingMoreRef.current.startedFetching || fetching;
        if ((fetchingMoreRef.current.startedFetching && !fetching) ||
            fetchingMoreRef.current.length !== tableProvider.list?.data?.length ||
            fetchingMoreRef.current.count !== tableProvider.list?.meta?.resultsCount) {
          fetchingMoreRef.current = {
            length: tableProvider.list?.data?.length,
            count: tableProvider.list?.meta?.resultsCount
          };
        }

        if (!fetchingMoreRef.current.fetching && !fetching &&
            tableProvider.list?.meta?.resultsCount > tableProvider.list?.data?.length) {
          const selectedIndex = (tableProvider.list?.data ?? []).findIndex((item) => compareEvent?.(item, selected));
          if (selectedIndex !== -1 && (selectedIndex + 1) >= (tableProvider.list?.data?.length - 5)) {
            fetchingMoreRef.current.fetching = true;
            fetchNextEvent?.();
          }
        }
      }
    }
  }, [fetchMore, loaded, fetching, isItemPage, selected, fetchNextEvent, compareEvent,
    tableProvider.list?.data, tableProvider.list?.meta?.resultsCount]);
}
